import { useMemo, useState } from "react";
import { useAccount } from "wagmi";
import { type Address } from "viem";

import { DEFAULT_VENUE, type VenueId } from "@/config/contracts";
import { CADC, USDC } from "@/config/tokens";
import {
  computeAjnaRisk,
  computeRisk,
  type RiskMetrics,
} from "@/lib/loop/risk";
import {
  accruedBorrowAssets,
  accruedTotalBorrowAssets,
} from "@/lib/loop/morpho";
import { addBps, mulDiv, removeBps, toNumber, toRaw, toText } from "@/lib/units";

import { useAjnaBorrower } from "./use-ajna-borrower";
import { useAjnaPool } from "./use-ajna-pool";
import { cadcNeededForUsdc, useChainlinkFx } from "./use-chainlink-fx";
import { useMorphoMarket } from "./use-morpho-market";
import { useMorphoPosition } from "./use-morpho-position";
import { useTokenBalance } from "./use-token-balance";

/**
 * `wallet` — caller supplies USDC, contract calls `unwind(maxRepay, pull)`.
 * `collateral` — caller supplies 0 USDC, contract calls `unwindBySwap` which
 *   flash-loans the debt, swaps part of the collateral to settle the flash
 *   loan, and returns the rest.
 */
export type UnwindSource = "wallet" | "collateral";

export type UnwindFormState = {
  readonly repayText: string;
  readonly derivedCollateralText: string;
  readonly setRepayText: (s: string) => void;
  readonly setFullRepay: () => void;
  readonly venue: VenueId;
  readonly source: UnwindSource;
  /** Args for `wallet` source. */
  readonly maxRepayRaw: bigint;
  readonly collateralPullRaw: bigint;
  /** Args for `collateral` source — computed from chain reads + slippage. */
  readonly collateralToSwapRaw: bigint;
  readonly collateralToReturnRaw: bigint;
  readonly slippageBps: number;
  /** Morpho only — borrow shares to repay this tx. 0n = full debt. */
  readonly repaySharesPart: bigint;
  /** USDC asset amount being repaid this tx, raw 1e6. */
  readonly partialRepayAssetsRaw: bigint;
  /** Ajna only — USDC native amount (raw 1e6) the contract will flash-borrow. */
  readonly ajnaMaxRepayNativeRaw: bigint;

  /** User-facing inputs. */
  readonly collateralText: string;
  /**
   * Resulting target leverage for the position after this unwind.
   * `0` is the special sentinel for "close everything" (postCollateral = 0,
   * postDebt = 0). Otherwise must be in `[1, currentLeverage]`.
   */
  readonly targetLeverage: number;
  /** Slider domain. `currentLeverage` is the unchanged-leverage end. */
  readonly currentLeverage: number;
  /** Derived display string for "Will repay" preview. */
  readonly derivedRepayText: string;
  /**
   * Collateral-source only: ceiling on how much CADC the user can receive
   * back, regardless of target leverage. Hard upper bound is
   * `C − K × D` where `K` is the swap cost per USDC at FX + slippage +
   * safety. `null` if not yet computable (no FX feed loaded, no position,
   * or wallet source).
   */
  readonly maxReturnCadcHuman: number | null;

  readonly setVenue: (v: VenueId) => void;
  readonly setSource: (s: UnwindSource) => void;
  readonly setCollateralText: (s: string) => void;
  readonly setTargetLeverage: (n: number) => void;
  readonly setSlippageBps: (n: number) => void;
  readonly setFullWithdraw: () => void;
  readonly setFullClose: () => void;

  readonly existingCollateralRaw: bigint | null;
  readonly existingBorrowRaw: bigint | null;
  readonly hasDebt: boolean;
  readonly hasCollateral: boolean;
  readonly usdcBalanceRaw: bigint | null;
  readonly postCollateralRaw: bigint;
  readonly postBorrowRaw: bigint;
  readonly fullClose: boolean;
  readonly postRisk: RiskMetrics | null;
  readonly isValid: boolean;
  readonly error: string | null;
};

const REPAY_BUFFER_BPS = 50n; // +0.5% accrual buffer on `wallet` source repay.
const COLLATERAL_SAFETY_BPS = 100n; // +1% over Chainlink expectation for swap cushion.

const DEFAULT_SLIPPAGE_BPS = 30;
const FULL_CLOSE_SENTINEL = 0;

const USDC_UNIT_RAW = 10n ** BigInt(USDC.decimals); // 1 USDC, raw.

// CADC amounts are shown to 4 dp, so the "Max" prefill rounds the true ceiling
// to the nearest displayed unit — which can land just above it. Allow one
// display unit of slack in raw validation so the clean displayed value isn't
// rejected; the return leg is clamped to the exact max wei regardless.
const CADC_DISPLAY_DP = 4;
const CADC_DISPLAY_EPS_RAW = 10n ** BigInt(CADC.decimals - CADC_DISPLAY_DP);

export const useUnwindForm = (): UnwindFormState => {
  const [venue, setVenue] = useState<VenueId>(DEFAULT_VENUE);
  const [source, setSource] = useState<UnwindSource>("collateral");
  const [collateralText, setCollateralTextState] = useState("");
  const [repayText, setRepayTextState] = useState("");
  const [slippageBps, setSlippageBps] = useState(DEFAULT_SLIPPAGE_BPS);
  // -1 ⇒ "unset" → defaults to currentLeverage (no leverage change). 0 is the
  // explicit "full close" sentinel; positive values pin the post-position's
  // leverage.
  const [targetLeverageRaw, setTargetLeverageRaw] = useState<number>(-1);

  const { address: account } = useAccount();
  const isMorpho = venue === "morpho";

  // Venue-side reads.
  const marketQuery = useMorphoMarket();
  const market = isMorpho ? marketQuery.data ?? null : null;
  const positionQuery = useMorphoPosition(isMorpho ? account : undefined);
  const position = isMorpho ? positionQuery.data ?? null : null;

  const ajnaPoolQuery = useAjnaPool();
  const ajnaPool = !isMorpho ? ajnaPoolQuery.data ?? null : null;
  const ajnaBorrowerQuery = useAjnaBorrower(!isMorpho ? account : undefined);
  const ajnaBorrower = !isMorpho ? ajnaBorrowerQuery.data ?? null : null;

  const { balance: usdcBalance } = useTokenBalance(
    USDC,
    account as Address | undefined,
  );
  const fxQuery = useChainlinkFx();
  const fx = fxQuery.data ?? null;

  // Accrue Morpho interest off-chain to the fetch time so the debt figure
  // matches what the contract repays at execution. `lastUpdate` is the fixed
  // on-chain anchor; `dataUpdatedAt` (when this market read landed) is a stable
  // "now" that advances on each refetch, and the residual drift to submit time
  // is absorbed by the repay/swap safety buffers.
  const nowSec = BigInt(Math.floor(marketQuery.dataUpdatedAt / 1000));
  const accruedMorphoBorrowTotal = useMemo<bigint>(
    () =>
      market
        ? accruedTotalBorrowAssets(market.state, market.borrowRatePerSec, nowSec)
        : 0n,
    [market, nowSec],
  );
  const morphoBorrowAssets = useMemo<bigint>(
    () =>
      market && position
        ? accruedBorrowAssets(
            position.borrowShares,
            market.state.totalBorrowShares,
            accruedMorphoBorrowTotal,
          )
        : 0n,
    [market, position, accruedMorphoBorrowTotal],
  );

  // Ajna currentDebtWad = t0debt × inflator / 1e18; ÷1e12 → USDC native raw.
  const ajnaCurrentDebtRaw = useMemo<bigint>(() => {
    if (!ajnaPool || !ajnaBorrower) return 0n;
    if (ajnaBorrower.t0debt === 0n) return 0n;
    const wad = (ajnaBorrower.t0debt * ajnaPool.inflator) / 10n ** 18n;
    return wad / 10n ** 12n;
  }, [ajnaPool, ajnaBorrower]);

  const existingBorrowRaw = isMorpho ? morphoBorrowAssets : ajnaCurrentDebtRaw;
  const existingCollateralRaw = isMorpho
    ? position?.collateral ?? 0n
    : ajnaBorrower?.collateral ?? 0n;

  // USDC value per 1 CADC (human units), from the venue's own oracle.
  //   Morpho: collateralValueRaw (USDC) = collateralRaw × oraclePrice / 1e36
  //           ⇒ P_human = oraclePrice / 1e24
  //   Ajna:   LUP is WAD quote-per-collateral
  //           ⇒ P_human = lup / 1e18
  const priceUsdcPerCadc = useMemo<number>(() => {
    if (isMorpho) {
      if (!market) return 0;
      return Number(market.oraclePrice) / 1e24;
    }
    if (!ajnaPool) return 0;
    return Number(ajnaPool.lup) / 1e18;
  }, [isMorpho, market, ajnaPool]);

  // Float views of the on-chain position, used only for the leverage-target
  // solver and display. Contract-bound amounts are derived from the raw
  // bigints, never from these.
  const cHuman = toNumber(existingCollateralRaw, CADC.decimals);
  const dHuman = toNumber(existingBorrowRaw, USDC.decimals);

  // Leverage = (C × P) / (C × P − D). For D = 0 ⇒ 1×.
  const currentLeverage = useMemo<number>(() => {
    if (cHuman === 0 || priceUsdcPerCadc === 0) return 1;
    const exposure = cHuman * priceUsdcPerCadc;
    const equity = exposure - dHuman;
    if (equity <= 0) return 1;
    return exposure / equity;
  }, [cHuman, dHuman, priceUsdcPerCadc]);

  // Default `targetLeverage` to the unchanged-leverage end whenever the user
  // hasn't explicitly moved the slider.
  const targetLeverage =
    targetLeverageRaw < 0 ? currentLeverage : targetLeverageRaw;
  const isFullClose = targetLeverage === FULL_CLOSE_SENTINEL;

  // Per-USDC "swap cost factor" K — how much CADC (raw 1e18) the contract
  // sells to receive 1 USDC at Chainlink + slippage + safety buffer. Computed
  // in bigint so it can size contract-bound legs without a float round-trip;
  // the float `kCadcPerUsdc` below is derived from it for the solver/display.
  const kRaw = fx
    ? addBps(
        cadcNeededForUsdc(USDC_UNIT_RAW, fx),
        BigInt(slippageBps) + COLLATERAL_SAFETY_BPS,
      )
    : 0n;
  const kCadcPerUsdc = toNumber(kRaw, CADC.decimals);

  // CADC (raw) the contract must sell to settle the whole debt: K × D.
  const kTimesDebtRaw = mulDiv(kRaw, existingBorrowRaw, USDC_UNIT_RAW);

  // Collateral-source ceiling on user-receivable CADC, regardless of L:
  // at W_max the position fully closes (postCollateral = 0 because the
  // contract sold all leftover C − W to settle the flash loan).
  //   W_max = C − K × D
  const maxReturnCadcRaw =
    existingCollateralRaw > kTimesDebtRaw
      ? existingCollateralRaw - kTimesDebtRaw
      : 0n;
  const maxReturnCadcHuman = useMemo<number | null>(() => {
    if (source !== "collateral") return null;
    if (kRaw === 0n) return null;
    return toNumber(maxReturnCadcRaw, CADC.decimals);
  }, [source, kRaw, maxReturnCadcRaw]);

  // Form input becomes raw at the boundary. `wTypedRaw` is the user's chosen
  // CADC withdrawal (received directly on wallet source, or the *return* leg
  // on collateral source where the contract takes collateralToSwap on top to
  // settle the flash loan); `rTypedRaw` is the typed USDC repay. The float
  // views feed only the leverage solver and validation thresholds.
  const wTypedRaw = toRaw(collateralText, CADC.decimals);
  const rTypedRaw = toRaw(repayText, USDC.decimals);
  const wTypedHuman = toNumber(wTypedRaw, CADC.decimals);
  const rTypedHuman = toNumber(rTypedRaw, USDC.decimals);

  // ─── Derive contract args from (W, L_target) ─────────────────────────────
  // Wallet source:
  //   postCollateral = C − W
  //   postDebt       = postCollateral × P × (1 − 1/L)
  //   maxRepay       = D − postDebt  (plus accrual buffer)
  //   collateralPull = W (default: C if blank)
  //
  // Collateral source: postCollateral = C − W − swap; same postDebt eqn,
  // plus the swap recurrence:  swap = K × (D − (C − W) × γ) / (1 − Kγ),
  // γ = P × (1 − 1/L).
  const γ = isFullClose ? 0 : priceUsdcPerCadc * (1 - 1 / targetLeverage);

  // Wallet source: R is the typed value; W is derived from R + L.
  const walletRepayHuman = isFullClose
    ? dHuman
    : Math.min(Math.max(0, rTypedHuman), dHuman);

  const walletWithdrawHuman = useMemo<number>(() => {
    if (isFullClose) return cHuman;
    if (γ <= 0) return 0;
    const w = cHuman - (dHuman - walletRepayHuman) / γ;
    return Math.max(0, Math.min(cHuman, w));
  }, [isFullClose, γ, cHuman, dHuman, walletRepayHuman]);

  // Effective `wHuman` used everywhere downstream.
  const wHuman = useMemo<number>(() => {
    if (source === "wallet") return walletWithdrawHuman;
    if (isFullClose) return cHuman;
    if (maxReturnCadcHuman != null) {
      return Math.min(wTypedHuman, maxReturnCadcHuman);
    }
    if (wTypedHuman > 0) return Math.min(wTypedHuman, cHuman);
    return 0;
  }, [
    source,
    walletWithdrawHuman,
    isFullClose,
    cHuman,
    wTypedHuman,
    maxReturnCadcHuman,
  ]);

  const collateralSwapHuman = useMemo<number>(() => {
    if (kCadcPerUsdc === 0) return 0;
    if (isFullClose) {
      // Sell enough CADC to cover the full debt via the swap.
      return Math.min(cHuman, dHuman * kCadcPerUsdc);
    }
    const denom = 1 - kCadcPerUsdc * γ;
    if (denom <= 0) return 0;
    const num = kCadcPerUsdc * (dHuman - Math.max(0, cHuman - wHuman) * γ);
    return Math.max(0, num / denom);
  }, [kCadcPerUsdc, isFullClose, dHuman, cHuman, wHuman, γ]);

  // ─── Bigint args fed to the contract ─────────────────────────────────────
  // `collateralPull` is the wallet-source `unwind` arg. Full close pulls the
  // exact on-chain collateral; a partial pull comes from the leverage solver
  // (genuinely fractional) and is clamped to never exceed the position.
  const collateralPullRaw = useMemo<bigint>(() => {
    if (isFullClose) return existingCollateralRaw;
    if (source === "wallet" || wTypedHuman > 0) {
      const pull = toRaw(walletWithdrawHuman, CADC.decimals);
      return pull > existingCollateralRaw ? existingCollateralRaw : pull;
    }
    return existingCollateralRaw;
  }, [isFullClose, source, wTypedHuman, walletWithdrawHuman, existingCollateralRaw]);

  const repayBaseRaw = isFullClose
    ? existingBorrowRaw
    : rTypedRaw > existingBorrowRaw
      ? existingBorrowRaw
      : rTypedRaw;
  const maxRepayRaw =
    repayBaseRaw === 0n ? 0n : addBps(repayBaseRaw, REPAY_BUFFER_BPS);

  // Raw collateral legs for the `collateral` (flash-swap) source. These are
  // contract-bound and must satisfy Morpho's exact check
  // `collateralToSwap + collateralToReturn <= pos.collateral`; reconstructing
  // them from float64 human units overshoots by dust and reverts. So:
  //   • full close — swap = min(C, K·D) and the return leg is the *exact*
  //     bigint remainder, so the legs sum to the position collateral exactly;
  //   • partial — the swap comes from the (fractional) leverage solver, the
  //     return is the user's raw typed amount capped at the flash-loan ceiling,
  //     and the pair is finally clamped so it can never exceed the position.
  const { collateralToSwapRaw, collateralToReturnRaw } = useMemo<{
    collateralToSwapRaw: bigint;
    collateralToReturnRaw: bigint;
  }>(() => {
    if (isFullClose) {
      const swap =
        kTimesDebtRaw > existingCollateralRaw
          ? existingCollateralRaw
          : kTimesDebtRaw;
      return {
        collateralToSwapRaw: swap,
        collateralToReturnRaw: existingCollateralRaw - swap,
      };
    }
    let swap = toRaw(collateralSwapHuman, CADC.decimals);
    if (swap > existingCollateralRaw) swap = existingCollateralRaw;
    let ret = wTypedRaw < maxReturnCadcRaw ? wTypedRaw : maxReturnCadcRaw;
    if (swap + ret > existingCollateralRaw) ret = existingCollateralRaw - swap;
    return { collateralToSwapRaw: swap, collateralToReturnRaw: ret };
  }, [
    isFullClose,
    kTimesDebtRaw,
    existingCollateralRaw,
    collateralSwapHuman,
    wTypedRaw,
    maxReturnCadcRaw,
  ]);

  // Actual debt the contract repays this tx (for displays + risk preview).
  // Wallet source: user-funded repay = D − postDebt with postCollateral = C − W.
  // Collateral source: the flash-borrow IS the repay; settle the loan by
  // selling K·repay collateral. From swap = K·repay and the leverage
  // condition postDebt = (C−W−swap)·γ, we get repay = swap/K.
  const partialRepayAssetsRaw = useMemo<bigint>(() => {
    if (existingBorrowRaw === 0n) return 0n;
    if (isFullClose) return existingBorrowRaw;
    // Collateral source: the swap settles the flash loan, so repay = swap / K
    // (inverse of K = CADC per USDC), all in bigint. Wallet source: the typed
    // repay, capped at the outstanding debt.
    const repaid =
      source === "collateral" && kRaw > 0n
        ? mulDiv(collateralToSwapRaw, USDC_UNIT_RAW, kRaw)
        : rTypedRaw;
    return repaid > existingBorrowRaw ? existingBorrowRaw : repaid;
  }, [
    existingBorrowRaw,
    isFullClose,
    source,
    kRaw,
    collateralToSwapRaw,
    rTypedRaw,
  ]);

  const repaySharesPart = useMemo<bigint>(() => {
    if (isFullClose) return 0n;
    if (!market || !position) return 0n;
    if (accruedMorphoBorrowTotal === 0n) return 0n;
    if (partialRepayAssetsRaw >= existingBorrowRaw) return 0n;
    // shares = assets × totalBorrowShares / accruedTotalBorrowAssets, rounded
    // up. Uses the accrued total so the conversion matches the debt figure.
    const num =
      partialRepayAssetsRaw * market.state.totalBorrowShares +
      accruedMorphoBorrowTotal -
      1n;
    return num / accruedMorphoBorrowTotal;
  }, [
    isFullClose,
    market,
    position,
    partialRepayAssetsRaw,
    existingBorrowRaw,
    accruedMorphoBorrowTotal,
  ]);

  const ajnaMaxRepayNativeRaw =
    partialRepayAssetsRaw === 0n
      ? 0n
      : addBps(partialRepayAssetsRaw, REPAY_BUFFER_BPS);

  // ─── Setters ─────────────────────────────────────────────────────────────
  const setCollateralText = (s: string) => setCollateralTextState(s);
  const setRepayText = (s: string) => setRepayTextState(s);
  const setSourceAndSeed = (s: UnwindSource) => {
    setSource(s);
    // Surface the currently-effective value (0) in the editable input so the
    // user can see what the math is using rather than an empty placeholder.
    if (s === "collateral" && collateralText === "") {
      setCollateralTextState("0");
    } else if (s === "wallet" && repayText === "") {
      setRepayTextState("0");
    }
  };

  // Helper: convert a human-units max to display text appropriate per source.
  const maxWithdrawTextForSource = (): string => {
    if (source === "collateral" && maxReturnCadcHuman != null) {
      return maxReturnCadcHuman.toFixed(4);
    }
    return toText(existingCollateralRaw, CADC.decimals, 4);
  };
  const fullRepayText = (): string =>
    toText(existingBorrowRaw, USDC.decimals, 2);

  const setTargetLeverage = (n: number) => {
    if (n === FULL_CLOSE_SENTINEL) {
      setTargetLeverageRaw(0);
      setCollateralTextState(maxWithdrawTextForSource());
      setRepayTextState(fullRepayText());
      return;
    }
    // Leaving Full close: drop the prefilled at-max value (which would pin
    // the math), but only if the user hasn't typed over it. Otherwise
    // preserve their input so the slider just adjusts the other field.
    const wasFullClose = targetLeverageRaw === FULL_CLOSE_SENTINEL;
    setTargetLeverageRaw(n);
    if (wasFullClose) {
      if (source === "collateral") {
        if (collateralText === maxWithdrawTextForSource()) {
          setCollateralTextState("0");
        }
      } else if (repayText === fullRepayText()) {
        setRepayTextState("");
      }
    }
  };
  // Largest R the user can afford given wallet balance + the buffered
  // approval the contract path needs. Returns null if no balance read yet.
  const affordableRepayText = (): string | null => {
    if (usdcBalance == null) return null;
    const cap = removeBps(usdcBalance.raw, REPAY_BUFFER_BPS);
    const bounded = cap > existingBorrowRaw ? existingBorrowRaw : cap;
    return toText(bounded, USDC.decimals, 2);
  };

  const setFullWithdraw = () => {
    if (source === "wallet") {
      if (existingBorrowRaw === 0n) return;
      // Prefer wallet-balance cap so we don't immediately trigger the
      // insufficient-balance error.
      setRepayTextState(affordableRepayText() ?? fullRepayText());
      return;
    }
    // Collateral source: "Max receive" always degenerates to a full close
    // (W = C − K·D consumes the rest of the collateral via the swap), so
    // route it through the Full close sentinel for a coherent summary.
    if (existingCollateralRaw === 0n) return;
    setTargetLeverageRaw(0);
    setCollateralTextState(maxWithdrawTextForSource());
    setRepayTextState(fullRepayText());
  };
  const setFullRepay = () => {
    if (existingBorrowRaw === 0n) return;
    setRepayTextState(affordableRepayText() ?? fullRepayText());
  };
  const setFullClose = () => {
    setTargetLeverageRaw(0);
    setCollateralTextState(maxWithdrawTextForSource());
    setRepayTextState(fullRepayText());
  };

  // ─── post-unwind state + risk ────────────────────────────────────────────
  const { postCollateralRaw, postBorrowRaw } = useMemo(() => {
    let postCollateral: bigint;
    let postBorrow: bigint;
    if (source === "wallet") {
      postCollateral =
        existingCollateralRaw > collateralPullRaw
          ? existingCollateralRaw - collateralPullRaw
          : 0n;
      const applied =
        maxRepayRaw > existingBorrowRaw ? existingBorrowRaw : maxRepayRaw;
      postBorrow =
        existingBorrowRaw > applied ? existingBorrowRaw - applied : 0n;
    } else {
      const consumed = collateralToSwapRaw + collateralToReturnRaw;
      postCollateral =
        existingCollateralRaw > consumed
          ? existingCollateralRaw - consumed
          : 0n;
      postBorrow =
        existingBorrowRaw > partialRepayAssetsRaw
          ? existingBorrowRaw - partialRepayAssetsRaw
          : 0n;
    }
    return { postCollateralRaw: postCollateral, postBorrowRaw: postBorrow };
  }, [
    source,
    existingCollateralRaw,
    existingBorrowRaw,
    maxRepayRaw,
    collateralPullRaw,
    collateralToSwapRaw,
    collateralToReturnRaw,
    partialRepayAssetsRaw,
  ]);

  const fullClose =
    (existingBorrowRaw > 0n || existingCollateralRaw > 0n) &&
    postCollateralRaw === 0n &&
    postBorrowRaw === 0n;

  const postRisk = useMemo<RiskMetrics | null>(() => {
    if (postCollateralRaw === 0n && postBorrowRaw === 0n) return null;
    if (isMorpho) {
      if (!market) return null;
      return computeRisk({
        collateralRaw: postCollateralRaw,
        borrowRaw: postBorrowRaw,
        oraclePrice: market.oraclePrice,
        lltv: market.params.lltv,
        loanDecimals: USDC.decimals,
        collateralDecimals: CADC.decimals,
      });
    }
    if (!ajnaPool) return null;
    return computeAjnaRisk({
      collateralRaw: postCollateralRaw,
      debtUsdcRaw: postBorrowRaw,
      lup: ajnaPool.lup,
    });
  }, [isMorpho, market, ajnaPool, postCollateralRaw, postBorrowRaw]);

  // ─── Validation ─────────────────────────────────────────────────────────
  const error = useMemo<string | null>(() => {
    if (account == null) return null;
    if (existingCollateralRaw === 0n && existingBorrowRaw === 0n) {
      return "No open position to close.";
    }
    if (!isFullClose && targetLeverage > currentLeverage + 1e-6) {
      return "Target leverage above current — pick a lower value.";
    }
    if (!isFullClose && targetLeverage < 1) {
      return "Target leverage must be at least 1× (or 0 for full close).";
    }
    if (source === "wallet") {
      if (rTypedHuman > dHuman + 1e-9) {
        return "Repaying more USDC than the position owes.";
      }
      if (
        maxRepayRaw > 0n &&
        usdcBalance != null &&
        usdcBalance.raw < maxRepayRaw
      ) {
        return "Insufficient USDC balance for the required repay amount.";
      }
      return null;
    }
    if (wTypedRaw > existingCollateralRaw + CADC_DISPLAY_EPS_RAW) {
      return "Withdrawing more collateral than the position holds.";
    }
    if (slippageBps <= 0 || slippageBps > 500) {
      return "Slippage must be between 1 and 500 bps.";
    }
    if (fx == null) return null;
    if (maxReturnCadcHuman != null && maxReturnCadcRaw === 0n) {
      return "Position can't be unwound via flash loan: debt × swap rate exceeds collateral. Use the wallet source instead.";
    }
    // Compare in raw with one display unit of slack: a "Max" prefill rounded to
    // 4 dp can sit just above the exact ceiling, and the leg clamps to the max
    // wei anyway, so only a meaningful over-entry should error.
    if (
      maxReturnCadcHuman != null &&
      wTypedRaw > maxReturnCadcRaw + CADC_DISPLAY_EPS_RAW
    ) {
      return `Max receivable via flash loan is ${maxReturnCadcHuman.toFixed(CADC_DISPLAY_DP)} ${CADC.symbol}.`;
    }
    return null;
  }, [
    account,
    existingCollateralRaw,
    existingBorrowRaw,
    isFullClose,
    targetLeverage,
    currentLeverage,
    wTypedRaw,
    rTypedHuman,
    dHuman,
    source,
    maxRepayRaw,
    usdcBalance,
    slippageBps,
    fx,
    maxReturnCadcHuman,
    maxReturnCadcRaw,
  ]);

  const isValid =
    error == null &&
    (source === "wallet"
      ? collateralPullRaw > 0n || maxRepayRaw > 0n
      : collateralToSwapRaw > 0n || existingBorrowRaw === 0n);

  // ─── Derived display strings ────────────────────────────────────────────
  const derivedRepayText = isFullClose
    ? toText(existingBorrowRaw, USDC.decimals, 2)
    : toText(partialRepayAssetsRaw, USDC.decimals, 2);
  const derivedCollateralText = isFullClose
    ? toText(existingCollateralRaw, CADC.decimals, 4)
    : walletWithdrawHuman > 0
      ? walletWithdrawHuman.toFixed(4)
      : "0.0000";

  return {
    venue,
    source,
    maxRepayRaw,
    collateralPullRaw,
    collateralToSwapRaw,
    collateralToReturnRaw,
    slippageBps,
    repaySharesPart,
    partialRepayAssetsRaw,
    ajnaMaxRepayNativeRaw,
    collateralText,
    repayText,
    targetLeverage,
    currentLeverage,
    derivedRepayText,
    derivedCollateralText,
    maxReturnCadcHuman,
    setVenue: (v: VenueId) => {
      setVenue(v);
    },
    setSource: setSourceAndSeed,
    setCollateralText,
    setRepayText,
    setTargetLeverage,
    setSlippageBps,
    setFullWithdraw,
    setFullRepay,
    setFullClose,
    existingCollateralRaw:
      (isMorpho ? position : ajnaBorrower) ? existingCollateralRaw : null,
    existingBorrowRaw:
      (isMorpho ? position : ajnaBorrower) ? existingBorrowRaw : null,
    hasDebt: existingBorrowRaw > 0n,
    hasCollateral: existingCollateralRaw > 0n,
    usdcBalanceRaw: usdcBalance?.raw ?? null,
    postCollateralRaw,
    postBorrowRaw,
    fullClose,
    postRisk,
    isValid,
    error,
  };
};
